import React, { useEffect, useState } from 'react';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { StyledPageTitle } from 'theme/styles';
import FormWrapper from 'components/atoms/FormWrapper';
import { ERouteLinks } from 'models/route';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';
import { useQuery } from 'hooks/useQuery';
import { ELegalEntityTypes } from 'constants/LegalEntityTypes';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { LegalEntityGroupForm } from 'pages/LegalEntitiesPage/LegalEntityGroupForm';
import { ILegalEntityGroupFormValues } from 'models/form';
import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { Table } from 'components/molecules/Table';
import {
  legalEntityGroupFirearmTableColumns,
  mapLEGroupFirearmListToDataSource,
} from 'pages/LegalEntitiesPage/helpers';
import { CardList } from 'components/molecules/CardList';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { LegalEntityGroupFirearmTableCard } from 'pages/LegalEntitiesPage/LegalEntityGroupFirearmTableCard';
import { ActionTypes } from 'state/actions/alert';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { useAlert } from 'models/alertContext';
import { IFirearm } from 'models/firearm';
import { useTranslations } from 'hooks/useTranslations';
import { useFirearms } from 'pages/Firearms/hooks';
import {
  renderDataColor,
  renderRowColor,
  renderRowColorHover,
} from 'components/molecules/Table/helpers';
import { ITableDataSource } from 'models/table';
import { useLang } from 'models/langContext';
import { useDictionary } from 'models/dictionaryContext';

const LegalEntityAddGroupPage = () => {
  const { id = '' } = useParams<{ id: string }>();
  const query = useQuery();
  const type: string | null = query.get('type')?.trim() || null;
  const { selectedLanguage } = useLang();

  const { firearms } = useFirearms();
  const { handleCreateGroup, navigateToDetails } = useLegalEntities();
  const { isDesktop } = useThemeBreakpoint();
  const { legalEntityAlert, nextPageAlert, clearAlert, setAlert } = useAlert();
  const navigate = useNavigate();
  const { state: routerState }: any = useLocation();
  const [selectedFirearms, setSelectedFirearms] = useState<IFirearm[]>([]);
  const [groupName, setGroupName] = useState<string>(routerState?.groupName || '');
  const [stableInitialFormValues] = useState<ILegalEntityGroupFormValues>({
    name: routerState?.groupName || '',
    uniqueId: '',
    firearmIds: [],
  });
  const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);
  const { t } = useTranslations();

  const { getMake, make, getModel, model, getCaliber, caliber, getState, state } =
    useDictionary();

  useEffect(() => {
    (async function initDictionaries() {
      getMake();
      getModel();
      getCaliber();
      getState();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    window.history.replaceState({}, document.title);
  }, []);

  useEffect(() => {
    (async function initFirearmsData() {
      if (routerState?.selectedFirearms?.length) {
        setSelectedFirearms(routerState.selectedFirearms);
      }
    })();
  }, [routerState]);

  useEffect(() => {
    if (firearms.length) {
      setSelectedFirearms(
        firearms.map((firearm) => ({
          ...firearm,
          id: firearm.uniqueId,
        })),
      );
    }
  }, [firearms]);

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_NEXT_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleSubmit = async (data: ILegalEntityGroupFormValues) => {
    try {
      await handleCreateGroup({
        ...data,
        uniqueId: id,
        ...(routerState?.selectedFirearms?.length
          ? {
              firearmIds: routerState?.selectedFirearms.map((item: IFirearm) => item.uniqueId),
            }
          : []),
      });
    } catch (e: any) {
      const message = e.response.data.errors
        ? e.response.data.errors[0].message
        : e.response.data.message;
      setAlert(ActionTypes.SET_LEGAL_ENTITY_ALERT, {
        text: message,
        variant: EAlertVariants.error,
      });
      setIsSubmitLoading(false);
    }
  };

  const handleDeleteSelectedFirearm = (firearmId: string) => {
    setSelectedFirearms((prevState) =>
      prevState.filter((item) => item.uniqueId !== firearmId),
    );
    routerState.selectedFirearms = routerState.selectedFirearms.filter(
      (item: IFirearm) => item.uniqueId !== firearmId,
    );
  };

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t('module.legalEntity.name'), route: ERouteLinks.LegalEntities },
          {
            label:
              type === ELegalEntityTypes.individual
                ? t('legalEntities.individual.accordion.label')
                : t('legalEntities.organization.accordion.label'),
            route: `${ERouteLinks.LegalEntities}?expand=${
              type === ELegalEntityTypes.individual ? 'individual' : 'organisation'
            }`,
          },
          {
            label: t('legalEntityDetails.legalEntityDetails.text'),
            route: `${ERouteLinks.ViewLegalEntity.replace(
              ':id',
              id,
            )}?type=${type}&expand=groups`,
          },
          t('addGroup.header'),
        ]}
      />
      <StyledPageTitle variant="h4">{t('addGroup.header')}</StyledPageTitle>

      <FormWrapper>
        <LegalEntityGroupForm
          onCancel={navigateToDetails}
          onSubmit={handleSubmit}
          onNameChange={(name: string) => {
            setGroupName(name);
          }}
          initialFormValues={stableInitialFormValues}
          isSubmitLoading={isSubmitLoading}
          setIsSubmitLoading={setIsSubmitLoading}
        >
          <>
            <StyledPageTitle variant="h4">
              {t('addGroup.firearmList.section.label')}
            </StyledPageTitle>
            <Typography variant="body1" sx={{ margin: '20px 0' }}>
              {t('addGroup.firearmList.section.info.text')}
            </Typography>
            <Grid size={{ xs: 12, lg: 8, xl: 6 }}>
              <Button
                id="search-firearm-button"
                label={t('addGroup.firearmList.section.searchFirearm.button')}
                variant={EButtonVariants.outlined}
                fullWidth
                onClick={() =>
                  navigate(
                    `${ERouteLinks.LegalEntityGroupFirearmSearch.replace(
                      ':id',
                      id,
                    )}?type=${type}&expand=groups`,
                    {
                      state: {
                        isDetailsContext: false,
                        groupName,
                        fromAddPage: true,
                        prevSelectedFirearms: routerState?.selectedFirearms,
                      },
                    },
                  )
                }
              />
            </Grid>
            {selectedFirearms?.length ? (
              <Box sx={{ marginTop: '20px' }}>
                {isDesktop ? (
                  <Table
                    columns={legalEntityGroupFirearmTableColumns({
                      handleDelete: (firearmId: string) =>
                        handleDeleteSelectedFirearm(firearmId),
                      renderStateFieldColor: (data: ITableDataSource) =>
                        renderDataColor(data).stateField,
                    })}
                    dataSource={mapLEGroupFirearmListToDataSource(
                      selectedFirearms,
                      make,
                      model,
                      caliber,
                      state,
                    )}
                    specificRowColor={renderRowColor}
                    specificRowColorHover={renderRowColorHover}
                  />
                ) : (
                  <CardList
                    items={mapLEGroupFirearmListToDataSource(
                      selectedFirearms,
                      make,
                      model,
                      caliber,
                      state,
                    )}
                    render={(data, index) => (
                      <Box key={data.uniqueId} sx={{ mb: 2 }}>
                        <LegalEntityGroupFirearmTableCard
                          data={data}
                          key={index}
                          renderDataColor={() => renderDataColor(data)}
                          deleteAction={(firearmId: string) =>
                            handleDeleteSelectedFirearm(firearmId)
                          }
                          hideManagement={false}
                        />
                      </Box>
                    )}
                  />
                )}
              </Box>
            ) : null}
          </>
        </LegalEntityGroupForm>
      </FormWrapper>
      {legalEntityAlert && (
        <Box sx={{ mt: 2 }}>
          <Alert text={legalEntityAlert.text} variant={legalEntityAlert.variant} />
        </Box>
      )}
      {nextPageAlert && (
        <Box sx={{ mt: 2 }}>
          <Alert text={nextPageAlert.text} variant={nextPageAlert.variant} />
        </Box>
      )}
    </>
  );
};

export { LegalEntityAddGroupPage };
